import { API_BASE_URL } from './config';
import { extractErrorMessage } from './errors';
import type {
  GroupResponse,
  GroupMemberResponse,
} from './types/groups';

export type { GroupResponse, GroupMemberResponse, GroupRef } from './types/groups';
export { ApiError } from './errors';

const BASE = `${API_BASE_URL}/api/v1/groups`;

function authHeaders(token: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function safeJson(res: Response): Promise<Record<string, unknown>> {
  try {
    const text = await res.text();
    if (!text) return {};
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function asArray<T>(data: unknown): T[] {
  return Array.isArray(data) ? (data as T[]) : [];
}

export async function listGroups(token: string): Promise<GroupResponse[]> {
  const res = await fetch(BASE, { headers: authHeaders(token) });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to list groups'));
  return asArray<GroupResponse>(data);
}

export async function createGroup(token: string, name: string): Promise<GroupResponse> {
  const res = await fetch(BASE, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ name }),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to create group'));
  return data as unknown as GroupResponse;
}

export async function renameGroup(
  token: string,
  groupId: string,
  name: string,
): Promise<GroupResponse> {
  const res = await fetch(`${BASE}/${groupId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ name }),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to rename group'));
  return data as unknown as GroupResponse;
}

export async function deleteGroup(token: string, groupId: string): Promise<void> {
  const res = await fetch(`${BASE}/${groupId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw new Error(extractErrorMessage(data, 'Failed to delete group'));
  }
}

export async function listGroupMembers(
  token: string,
  groupId: string,
): Promise<GroupMemberResponse[]> {
  const res = await fetch(`${BASE}/${groupId}/members`, { headers: authHeaders(token) });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to list group members'));
  return asArray<GroupMemberResponse>(data);
}

export async function addGroupMembers(
  token: string,
  groupId: string,
  userIds: string[],
): Promise<GroupMemberResponse[]> {
  const res = await fetch(`${BASE}/${groupId}/members`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ userIds }),
  });
  const data = await safeJson(res);
  if (!res.ok) throw new Error(extractErrorMessage(data, 'Failed to add group members'));
  return asArray<GroupMemberResponse>(data);
}

export async function removeGroupMember(
  token: string,
  groupId: string,
  userId: string,
): Promise<void> {
  const res = await fetch(`${BASE}/${groupId}/members/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw new Error(extractErrorMessage(data, 'Failed to remove group member'));
  }
}
